//@flow
import React, {
    useState,
    useCallback,
    useRef,
    useMemo,
    useEffect,
} from "react";
import { getDisplayName } from "data/constants";
import { MultiSelectionContext } from "data/context";
import produce from "immer";
import Bucket from "./Bucket";
import Checkbox from "./Checkbox";
import {
    MultiSelectionActions,
    MultiSelectionHandlers,
    MultiSelectionContextProps,
    MultiSelectionProps,
} from "data/types";

type Config = {
    delay: number,
};

type Props = {};

/* istanbul ignore next */
const isTouchEvent = (event) => {
    return "touches" in event;
};

/* istanbul ignore next */
const preventDefault = (event) => {
    if (!isTouchEvent(event)) return;

    if (event.touches.length < 2 && event.preventDefault) {
        event.preventDefault();
    }
};

const withMultiSelection =
    ({ delay = 300 }: Config) =>
    (WrappedComponent: ComponentType<any>) => {
        const HOC = (props: Props) => {
            const timeout = useRef();
            const target = useRef();
            const [multiple, setMultiple] = useState(false);
            const [activeRows, setActiveRows] = useState({});
            const [availableRows, setAvailableRows] = useState({});
            const enable = useCallback(() => {
                setMultiple(true);
            }, []);
            const disable = useCallback(() => {
                setMultiple(false);
                setActiveRows({});
            }, []);

            const isSelected = useCallback(
                (index) => activeRows.hasOwnProperty(index),
                [activeRows]
            );

            // Actions
            const handleAdd = useCallback(
                (id) =>
                    setActiveRows((activeRows) =>
                        produce(activeRows, (draft) => {
                            /* istanbul ignore else */
                            if (!draft.hasOwnProperty(id)) {
                                draft[id] = availableRows[id];
                                setMultiple(true);
                            }
                        })
                    ),
                [availableRows]
            );
            const handleRemove = useCallback(
                (id) =>
                    setActiveRows((activeRows) =>
                        produce(activeRows, (draft) => {
                            draft[id] = null;
                            delete draft[id];
                            Object.keys(draft).length === 0 &&
                                setMultiple(false);
                        })
                    ),
                []
            );
            const handleToggle = useCallback(
                (id) =>
                    setActiveRows((activeRows) =>
                        produce(activeRows, (draft) => {
                            if (draft.hasOwnProperty(id)) {
                                draft[id] = null;
                                delete draft[id];
                                Object.keys(draft).length === 0 &&
                                    setMultiple(false);
                            } else {
                                draft[id] = availableRows[id];
                                setMultiple(true);
                            }
                        })
                    ),
                [availableRows]
            );
            const handleSelectAll = useCallback(
                () =>
                    setActiveRows((activeRows) =>
                        produce(activeRows, (draft) => {
                            Object.keys(availableRows).forEach(
                                /* istanbul ignore next */
                                (id) => (draft[id] = availableRows[id])
                            );
                        })
                    ),
                [availableRows]
            );
            const handleDeselectAll = useCallback(() => setActiveRows({}), []);
            const handleInvert = useCallback(
                () =>
                    setActiveRows((activeRows) =>
                        produce(activeRows, (draft) => {
                            Object.keys(availableRows).forEach(
                                /* istanbul ignore next */ (id) => {
                                    if (draft.hasOwnProperty(id)) {
                                        draft[id] = null;
                                        delete draft[id];
                                    } else {
                                        draft[id] = availableRows[id];
                                    }
                                }
                            );
                        })
                    ),
                [availableRows]
            );

            // Handle Longpress
            const on = useCallback((e) => {
                target.current = e.target;
                /* istanbul ignore else */
                if (target.current) {
                    timeout.current = setTimeout(() => {
                        setMultiple(true);
                        e.target.addEventListener("touchend", preventDefault, {
                            passive: false,
                        });
                    }, delay);
                }
            }, []);

            const off = useCallback((e) => {
                timeout.current && clearTimeout(timeout.current);
                target.current &&
                    target.current.removeEventListener(
                        "touchend",
                        preventDefault
                    );
            }, []);

            const handler: MultiSelectionHandlers = useMemo(
                () => ({
                    onTouchEnd: off,
                    onTouchMove: off,
                    onTouchStart: on,
                }),
                [on, off]
            );

            // Handle Esc
            const handleEsc = useCallback(
                (e: SyntheticKeyboardEvent<*>) => {
                    /* istanbul ignore else */
                    if (e.keyCode === 27) {
                        e.preventDefault();
                        disable();
                    }
                },
                [disable]
            );
            useEffect(() => {
                window.document.addEventListener("keydown", handleEsc);
                return () => {
                    window.document.removeEventListener("keydown", handleEsc);
                };
            }, [handleEsc]);

            // UI for multi selection
            const renderedBucket = useMemo(
                () => (
                    <Bucket
                        multiple={multiple}
                        enable={enable}
                        disable={disable}
                        activeRows={activeRows}
                        availableRows={availableRows}
                        handleSelectAll={handleSelectAll}
                        handleDeselectAll={handleDeselectAll}
                        handleInvert={handleInvert}
                    />
                ),
                [
                    enable,
                    disable,
                    handleSelectAll,
                    handleDeselectAll,
                    handleInvert,
                    activeRows,
                    multiple,
                    availableRows,
                ]
            );

            // UI for checkbox
            const renderCheckbox = useCallback(
                (index, className) => (
                    <Checkbox
                        index={index}
                        className={className}
                        isSelected={isSelected}
                        handleToggle={handleToggle}
                    />
                ),
                [handleToggle, isSelected]
            );

            const actions: MultiSelectionActions = useMemo(
                () => ({
                    handleAdd,
                    handleRemove,
                    handleToggle,
                    handleSelectAll,
                    handleDeselectAll,
                    handleInvert,
                }),
                [
                    handleAdd,
                    handleRemove,
                    handleToggle,
                    handleSelectAll,
                    handleDeselectAll,
                    handleInvert,
                ]
            );

            const context: MultiSelectionContextProps = useMemo(
                () => ({
                    handler,
                    multiple,
                    actions,
                    isSelected,
                    renderCheckbox,
                    renderedBucket,
                }),
                [
                    handler,
                    multiple,
                    actions,
                    renderCheckbox,
                    isSelected,
                    renderedBucket,
                ]
            );

            const multiSelection: MultiSelectionProps = useMemo(
                () => ({
                    actions,
                    activeRows,
                    availableRows,
                    setAvailableRows,
                    enable,
                    disable,
                    handler,
                    multiple,
                    isSelected,
                }),
                [
                    handler,
                    enable,
                    disable,
                    actions,
                    activeRows,
                    multiple,
                    availableRows,
                    isSelected,
                ]
            );

            return (
                <MultiSelectionContext.Provider value={context}>
                    <WrappedComponent
                        {...props}
                        multiSelection={multiSelection}
                    />
                </MultiSelectionContext.Provider>
            );
        };
        HOC.displayName = `withMultiSelection(${getDisplayName(
            WrappedComponent
        )})`;
        return HOC;
    };

export default withMultiSelection;
